import React, { useEffect, useRef, useState } from "react";
import "./WantedRecommendRow.scss";
import { photoVariant } from "../../utils/imageUrl";

const formatCurrency = (currency) => {
  const raw = String(currency || "").trim();

  if (!raw) return "ГРН";
  if (raw === "UAH" || raw === "₴" || raw.toLowerCase() === "грн") return "ГРН";
  if (raw === "USD" || raw === "$") return "$";
  if (raw === "EUR" || raw === "€") return "€";
  if (raw === "PLN" || raw === "zł") return "zł";
  if (raw === "GBP" || raw === "£") return "£";

  return raw;
};

const getPhoto = (item) => {
  if (item?.mainPhoto) return item.mainPhoto;

  if (Array.isArray(item?.photos) && item.photos[0]) {
    return item.photos[0];
  }

  if (item?.photo) return item.photo;

  return "";
};

const getPriceLabel = (item) => {
  const price = item?.price;

  if (price === null || price === undefined || price === "") {
    return "Шукаю";
  }

  return `${price} ${formatCurrency(item?.currency)}`;
};

const posterThemes = [
  "blue",
  "violet",
  "graphite",
  "green",
  "bronze",
  "wine",
  "ocean",
  "sand",
];

const getStableTheme = (item, title) => {
  const source = String(item?.listingId || item?.id || title || "wanted");
  let hash = 0;

  for (let i = 0; i < source.length; i += 1) {
    hash = source.charCodeAt(i) + ((hash << 5) - hash);
  }

  const index = Math.abs(hash) % posterThemes.length;
  return posterThemes[index];
};

const WantedRecommendRow = ({ items = [], onOpenListing, onMore }) => {
  const scrollRef = useRef(null);

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const safeItems = Array.isArray(items) ? items.slice(0, 5) : [];

  const updateScrollState = () => {
    const el = scrollRef.current;
    if (!el) return;

    const { scrollLeft, scrollWidth, clientWidth } = el;

    setCanScrollLeft(scrollLeft > 5);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 5);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    updateScrollState();

    el.addEventListener("scroll", updateScrollState);
    window.addEventListener("resize", updateScrollState);

    return () => {
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [safeItems.length]);

  if (!safeItems.length) return null;

  const scrollWanted = (direction) => {
    const el = scrollRef.current;
    if (!el) return;

    el.scrollBy({
      left: direction === "left" ? -520 : 520,
      behavior: "smooth",
    });

    setTimeout(updateScrollState, 420);
  };

  return (
    <section className="wanted-recs" aria-label="Шукають зараз">
      <div className="wanted-recs__head">
        <div>
          <h3 className="wanted-recs__title">Шукають зараз</h3>
          <p className="wanted-recs__subtitle">
            Люди шукають товари, послуги або пропозиції
          </p>
        </div>
      </div>

      <div className="wanted-recs__wrap">
        {canScrollLeft && (
          <button
            type="button"
            className="wanted-recs__arrow wanted-recs__arrow--left"
            onClick={() => scrollWanted("left")}
            aria-label="Прокрутити вліво"
          >
            ‹
          </button>
        )}

        <div className="wanted-recs__scroll" ref={scrollRef}>
          {safeItems.map((item) => {
            const id = String(item?.listingId || item?.id || "");
            const photo = getPhoto(item);
            const title = item?.title || item?.description || "Шукаю";
            const location = item?.location || "";
            const theme = getStableTheme(item, title);

            return (
              <button
                key={id}
                type="button"
                className="wanted-recs__card"
                onClick={() => onOpenListing?.(item)}
              >
                <div className="wanted-recs__image-wrap">
                  {photo ? (
                    <img
                      className="wanted-recs__image"
                      src={photoVariant(photo, "sm")}
                      alt={title}
                      loading="lazy"
                    />
                  ) : (
                    <div
                      className={`wanted-recs__poster wanted-recs__poster--${theme}`}
                    >
                      <div className="wanted-recs__poster-glow" />
                      <div
                        className={`wanted-recs__poster-title ${
                          title.length <= 18
                            ? "is-short"
                            : title.length <= 42
                            ? "is-medium"
                            : "is-long"
                        }`}
                      >
                        {title}
                      </div>
                    </div>
                  )}
                </div>

                <div className="wanted-recs__body">
                  <div className="wanted-recs__name">{title}</div>

                  <div className="wanted-recs__meta">
                    <span className="wanted-recs__price">
                      {getPriceLabel(item)}
                    </span>

                    {location && (
                      <span className="wanted-recs__location">{location}</span>
                    )}
                  </div>
                </div>
              </button>
            );
          })}

          <button type="button" className="wanted-recs__more" onClick={onMore}>
            <span>Більше шукайків</span>
            <b>→</b>
          </button>
        </div>

        {canScrollRight && (
          <button
            type="button"
            className="wanted-recs__arrow wanted-recs__arrow--right"
            onClick={() => scrollWanted("right")}
            aria-label="Прокрутити вправо"
          >
            ›
          </button>
        )}
      </div>
    </section>
  );
};

export default WantedRecommendRow;